import React from 'react';
import { MessageCircleIcon, MailIcon, PhoneIcon, MapPinIcon, ArrowRightIcon } from 'lucide-react';
import { PressStrip } from '../components/PressStrip';
import { Reveal } from '../components/Reveal';
import { founder, philosophy, salon } from '../data/content';
import { useStore } from '../contexts/StoreContext';
const SALON_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/0ed04ad3-0600-47a3-bc34-389527c1fbf3.jpg';
const facts = [{
  value: '20+',
  label: 'jaar in de huidzorg'
}, {
  value: '6.000+',
  label: 'behandelingen gegeven'
}, {
  value: '24 uur',
  label: 'reactietijd op je bericht'
}];
export function About() {
  const {
    openBooking
  } = useStore();
  return <>
      {/* Intro */}
      <section className="bg-sand" data-id="element-265" data-mp="2x7wps445l3yd">
        <div className="mx-auto grid max-w-content gap-12 px-6 pb-16 pt-12 lg:grid-cols-12 lg:gap-16 lg:px-10 lg:pb-20 lg:pt-14" data-id="element-266" data-mp="2xbapf4lfmz24">
          <div className="lg:col-span-5" data-id="element-267" data-mp="1lukgvetjdv5c">
            <img src={founder.portraitPalm} alt={`${founder.name}, ${founder.role}`} className="arch aspect-[4/5] w-full object-cover" data-id="element-268" data-mp="1lvm3mgjx062w" />
          </div>
          <div className="lg:col-span-7 lg:pt-4" data-id="element-269" data-mp="1pkjlmzc34j91">
            <p className="rule-label text-[11px] uppercase tracking-widest text-clay" data-id="element-270" data-mp="23izryp61c7qn">
              Over Beauty Bewust
            </p>
            <h1 className="mt-7 font-display text-[2.7rem] font-light leading-[1.02] text-ink sm:text-6xl" data-id="element-271" data-mp="1yhtwijkzq6df">
              Hoi, ik ben <span className="italic text-clay" data-id="element-272" data-mp="2dejkr2payqb6">{founder.firstName}.</span>
            </h1>
            <div className="mt-8 max-w-xl space-y-5 text-[16px] leading-relaxed text-muted" data-id="element-273" data-mp="28hb2l245mm9x">
              <p data-id="element-274" data-mp="2m86dejy7rdg5">
                Twintig jaar geleden begon ik in een klassieke schoonheidssalon waar iedereen
                dezelfde behandeling kreeg. Dat wringde. De huid van iemand met hormonale acne
                vraagt iets heel anders dan een huid in de overgang — en beide vragen tijd.
              </p>
              <p data-id="element-275" data-mp="2r5y79jif59mz">
                Bij Beauty Bewust in {salon.city} zie ik één cliënt tegelijk. Ik werk met de
                cosmeceutical lijn emerginC: ingrediënten die bijna medicinaal zijn, zodat je huid
                echt verbetert. Daaromheen bindweefselmassage, ontspanning en — als je dat wilt —
                energetisch werk.
              </p>
              <p data-id="element-276" data-mp="22mt7fl9a41no">
                Wat ik beloof is geen wonder, maar wel eerlijkheid: over wat haalbaar is, hoe lang
                het duurt en wanneer je beter naar een arts kunt.
              </p>
            </div>

            <dl className="mt-10 grid gap-6 border-t border-line pt-8 sm:grid-cols-3" data-id="element-277" data-mp="2cie2t0jue3og">
              {facts.map(f => <div key={f.label} data-id="element-278" data-mp="2gjzonbectsas">
                  <dt className="sr-only" data-id="element-279" data-mp="2ll5hd980lfkb">{f.label}</dt>
                  <dd data-id="element-280" data-mp="2lijazcwv163o">
                    <span className="block font-display text-3xl font-light text-clay" data-id="element-281" data-mp="m81en7880sdu">
                      {f.value}
                    </span>
                    <span className="mt-1.5 block text-[13px] leading-snug text-muted" data-id="element-282" data-mp="r7eaghh2l9sk">
                      {f.label}
                    </span>
                  </dd>
                </div>)}
            </dl>
          </div>
        </div>
      </section>

      <PressStrip tone="shell" data-id="element-283" data-mp="9ws9lxqogmy0" />

      {/* Philosophy */}
      <section className="bg-sand py-20 lg:py-28" data-id="element-284" data-mp="9v7eo70gb1zm">
        <div className="mx-auto grid max-w-content gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-285" data-mp="4zicrx0yl905">
          <Reveal className="lg:col-span-7" data-id="element-286" data-mp="12l69yk58nvcq">
            <p className="text-[11px] uppercase tracking-widest text-clay" data-id="element-287" data-mp="12jv66kbsmnlm">Eerlijk vooraf</p>
            <blockquote className="mt-5 font-display text-4xl font-light leading-[1.08] text-ink sm:text-5xl" data-id="element-288" data-mp="xl1o0elka8f4">
              “{philosophy.claim}.”
            </blockquote>
            <p className="mt-7 max-w-xl text-[15px] leading-relaxed text-muted" data-id="element-289" data-mp="olohnusqhzym">
              {philosophy.body}
            </p>
          </Reveal>
          <div className="lg:col-span-5" data-id="element-290" data-mp="39cox5yrl6de7">
            <img src={founder.atWork} alt={`${founder.name} tijdens een behandeling`} className="aspect-square w-full object-cover" data-id="element-291" data-mp="39bvxfhr1oo5k" />
          </div>
        </div>
      </section>

      {/* Salon & contact */}
      <section className="border-t border-line bg-shell py-20 lg:py-28" data-id="element-292" data-mp="r6304qwennm5">
        <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-293" data-mp="r3qadrkeo8yj">
          <div className="max-w-2xl" data-id="element-294" data-mp="w5nxiwvxfnwr">
            <p className="text-[11px] uppercase tracking-widest text-clay" data-id="element-295" data-mp="w2jljdc7znvj">De salon</p>
            <h2 className="mt-4 font-display text-4xl font-light leading-[1.08] text-ink sm:text-[2.9rem]" data-id="element-296" data-mp="1jsdxztsmnaf9">
              {salon.city}, één behandelkamer.
            </h2>
            <p className="mt-5 text-[16px] leading-relaxed text-muted" data-id="element-297" data-mp="1at0nqywoihth">
              Geen wachtruimte vol mensen, geen wisselende specialisten. Kom een paar minuten voor
              je afspraak — er staat thee klaar.
            </p>
          </div>

          <div className="mt-12 grid gap-10 lg:grid-cols-12 lg:gap-14" data-id="element-298" data-mp="3g8na9c35z1w">
            <Reveal className="lg:col-span-7" data-id="element-299" data-mp="3e48gwcnffmu">
              <img src={SALON_IMAGE} alt={`De behandelkamer van Beauty Bewust in ${salon.city}`} className="aspect-[3/2] w-full object-cover" data-id="element-300" data-mp="3s1aofbgaygpw" />
            </Reveal>

            <div className="lg:col-span-5" data-id="element-301" data-mp="5tkfsskayvcg">
              <div className="border-t border-ink/70 pt-6" data-id="element-302" data-mp="vsqm6b1lnby">
                <h3 className="text-[11px] uppercase tracking-widest text-muted" data-id="element-303" data-mp="wby0t32gtbq">Bezoek</h3>
                <address className="mt-4 space-y-4 text-[14px] not-italic" data-id="element-304" data-mp="39cdqmxlp562x">
                  <p className="flex gap-3" data-id="element-305" data-mp="34daopm10jfz6">
                    <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} data-id="element-306" data-mp="34aepzvhccn9o" />
                    <span className="text-ink" data-id="element-307" data-mp="3j3i6s0n3ywzc">
                      Beauty Bewust
                      <br data-id="element-308" data-mp="3e945jhlrlvmr" />
                      {salon.city}, op afspraak
                    </span>
                  </p>
                  <p className="flex gap-3" data-id="element-309" data-mp="2qirqznw0l0n7">
                    <MailIcon className="mt-0.5 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} data-id="element-310" data-mp="2lkymr0e9ugpk" />
                    <a href={`mailto:${salon.email}`} className="text-muted transition-colors duration-150 ease-soft hover:text-ink" data-id="element-311" data-mp="3baynldblfr7y">
                      {salon.email}
                    </a>
                  </p>
                  <p className="flex gap-3" data-id="element-312" data-mp="3p1afbb49nnyk">
                    <PhoneIcon className="mt-0.5 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} data-id="element-313" data-mp="3p125xt7cr0s4" />
                    <span className="text-muted" data-id="element-314" data-mp="2scvt4j0a122r">{salon.phone}</span>
                  </p>
                </address>
                <p className="mt-5 text-[13px] leading-relaxed text-muted" data-id="element-315" data-mp="2wd682l61r2tr">
                  Vermeld in je mail even je telefoonnummer, dan neem ik binnen 24 uur contact met
                  je op.
                </p>
              </div>

              <div className="mt-9 space-y-3" data-id="element-316" data-mp="gm9zpgm7q7rq">
                <button type="button" onClick={() => openBooking()} className="flex w-full items-center justify-between rounded-full bg-ink px-6 py-3.5 text-sm text-shell transition-colors duration-150 ease-soft hover:bg-clay" data-id="element-317" data-mp="gl8rdjwkzzdp">
                  Boek Beauty Bewust online
                  <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} data-id="element-318" data-mp="1wct37tq3bci2" />
                </button>
                <a href={salon.whatsapp} className="flex w-full items-center justify-center gap-2.5 rounded-full border border-ink/20 px-6 py-3.5 text-sm text-ink transition-colors duration-150 ease-soft hover:border-ink" data-id="element-319" data-mp="1re9czh4grejw">
                  <MessageCircleIcon className="h-4 w-4" strokeWidth={1.5} data-id="element-320" data-mp="1ifnz3z9xsnpg" />
                  Stuur een WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>;
}